import { useCallback, useEffect, useState } from "react";
import { getQuoteApi, getQuotePdfLinkApi, getQuotesApi, uploadAgreementPdfApi, uploadQuoteWhatsAppPdfApi } from "../api/quoteApi";
import { normalizeQuoteItems } from "../utils/normalizeQuoteItems";
import { getQuoteCommercialTotals } from "../utils/quoteLineMath";
import { formatInr } from "../utils/formatInr";
import {
  generateAgreementPDF,
  generateAgreementPDFBlob,
  DEFAULT_AGREEMENT_TERMS,
  AGREEMENT_SECTIONS,
} from "../utils/generateAgreementPDF";
import { generateQuotePDFBlob } from "../utils/generateQuotePDF";

const fmtDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "-";

const clientName = (project) =>
  project?.client?.name || project?.clientName || "Client";

const clientEmail = (project) =>
  project?.client?.email || "";

const clientPhone = (project) =>
  project?.client?.phone || project?.client?.mobile || "";

const projectAddress = (project) => {
  const client = typeof project?.client === "object" ? project.client : {};
  return [
    project?.propertyAddress || client?.propertyAddress || client?.address?.street || client?.address?.line1,
    project?.city || client?.address?.city,
    project?.state || client?.address?.state,
    client?.address?.pincode,
  ].filter(Boolean).join(", ") || "Address not available";
};

export default function AgreementTab({ clientId, project }) {
  const [latestQuote, setLatestQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [agreementClientName, setAgreementClientName] = useState("");
  const [agreementClientEmail, setAgreementClientEmail] = useState("");
  const [agreementClientPhone, setAgreementClientPhone] = useState("");
  const [agreementAddress, setAgreementAddress] = useState("");
  const [additionalTerms, setAdditionalTerms] = useState("");
  const [agreementTerms, setAgreementTerms] = useState(() => ({ ...DEFAULT_AGREEMENT_TERMS }));
  const [collapsedSections, setCollapsedSections] = useState({});
  const [quotePdfUrl, setQuotePdfUrl] = useState("");
  const [copyingAgreementLink, setCopyingAgreementLink] = useState(false);

  const storageKey = project?._id ? `agreement_terms_v2_${project._id}` : "";

  useEffect(() => {
    if (project) {
      setAgreementClientName(clientName(project));
      setAgreementClientEmail(clientEmail(project));
      setAgreementClientPhone(clientPhone(project));
      setAgreementAddress(projectAddress(project));

      if (storageKey) {
        try {
          const saved = localStorage.getItem(storageKey);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed === "object") {
              const terms = { ...DEFAULT_AGREEMENT_TERMS, ...(parsed.terms || {}) };
              if (
                terms.paymentTerms &&
                (terms.paymentTerms.includes("40%") || terms.paymentTerms.includes("15%")) &&
                !terms.paymentTerms.includes("50% – Execution Commencement")
              ) {
                terms.paymentTerms = DEFAULT_AGREEMENT_TERMS.paymentTerms;
              }
              setAgreementTerms(terms);
              if (typeof parsed.additionalTerms === "string") {
                setAdditionalTerms(parsed.additionalTerms);
              }
            }
          } else {
            // Check legacy storage if custom additionalTerms existed
            const legacyKey = `agreement_terms_${project._id}`;
            const legacySaved = localStorage.getItem(legacyKey);
            if (legacySaved) {
              try {
                const legacyParsed = JSON.parse(legacySaved);
                if (typeof legacyParsed?.additionalTerms === "string") {
                  setAdditionalTerms(legacyParsed.additionalTerms);
                }
              } catch (_) {}
            } else {
              setAdditionalTerms("");
            }
            setAgreementTerms({ ...DEFAULT_AGREEMENT_TERMS });
          }
        } catch (err) {
          console.warn("Could not load saved agreement terms:", err);
          setAgreementTerms({ ...DEFAULT_AGREEMENT_TERMS });
        }
      }
    }
  }, [project, storageKey]);

  const saveTermsToStorage = (newTerms, newAddl) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          terms: newTerms,
          additionalTerms: newAddl !== undefined ? newAddl : additionalTerms,
        })
      );
    } catch (err) {
      console.warn("Could not save agreement terms to localStorage:", err);
    }
  };

  const handleTermChange = (key, value) => {
    const updated = { ...agreementTerms, [key]: value };
    setAgreementTerms(updated);
    saveTermsToStorage(updated, additionalTerms);
  };

  const resetSection = (key) => {
    const updated = { ...agreementTerms, [key]: DEFAULT_AGREEMENT_TERMS[key] };
    setAgreementTerms(updated);
    saveTermsToStorage(updated, additionalTerms);
  };

  const resetAllTerms = () => {
    if (window.confirm("Are you sure you want to reset all terms & conditions to standard defaults?")) {
      const updated = { ...DEFAULT_AGREEMENT_TERMS };
      setAgreementTerms(updated);
      setAdditionalTerms("");
      saveTermsToStorage(updated, "");
    }
  };

  const toggleCollapse = (key) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleAllCollapse = () => {
    const anyOpen = AGREEMENT_SECTIONS.some((sec) => !collapsedSections[sec.key]);
    if (anyOpen) {
      const next = {};
      AGREEMENT_SECTIONS.forEach((sec) => {
        next[sec.key] = true;
      });
      setCollapsedSections(next);
    } else {
      setCollapsedSections({});
    }
  };

  const modifiedCount = AGREEMENT_SECTIONS.filter(
    (sec) => (agreementTerms[sec.key] ?? "") !== (DEFAULT_AGREEMENT_TERMS[sec.key] ?? "")
  ).length;

  const loadLatestQuote = useCallback(async () => {
    if (!clientId || !project?._id) return;
    setLoading(true);
    try {
      const list = await getQuotesApi({ client: clientId, project: project._id, limit: 1, page: 1 });
      const quote = list.data?.data?.quotes?.[0] || null;
      if (!quote?._id) {
        setLatestQuote(null);
        setQuotePdfUrl("");
        return;
      }
      const fresh = (await getQuoteApi(quote._id, { params: { agreementTs: Date.now() } })).data?.data?.quote || quote;
      setLatestQuote({ ...fresh, items: normalizeQuoteItems(fresh.items || []) });
      try {
        const linkRes = await getQuotePdfLinkApi(quote._id);
        setQuotePdfUrl(linkRes.data?.data?.pdfUrl || "");
      } catch (linkErr) {
        console.error(linkErr);
        setQuotePdfUrl("");
      }
    } catch (e) {
      console.error(e);
      setLatestQuote(null);
      setQuotePdfUrl("");
      alert(e.response?.data?.message || e.message || "Could not load latest quotation");
    } finally {
      setLoading(false);
    }
  }, [clientId, project?._id]);

  useEffect(() => {
    loadLatestQuote();
  }, [loadLatestQuote]);

  const createUploadedQuotePdfLink = async (quote) => {
    const pdfBlob = await generateQuotePDFBlob(quote);
    const formData = new FormData();
    const clientNameForFile = String(quote?.client?.name || "Client").replace(/[\\/:*?"<>|]/g, "-");
    formData.append("pdf", pdfBlob, `Quotation_${clientNameForFile}.pdf`);
    const res = await uploadQuoteWhatsAppPdfApi(quote._id, formData);
    const pdfUrl = res.data?.data?.pdfUrl;
    if (!pdfUrl) throw new Error("Quotation link is not available");
    return pdfUrl;
  };

  const createAgreement = async () => {
    if (!latestQuote) {
      alert("No quotation found for this project.");
      return;
    }
    setGenerating(true);
    try {
      let actualQuotePdfUrl = quotePdfUrl || "";
      try {
        actualQuotePdfUrl = await createUploadedQuotePdfLink(latestQuote);
        setQuotePdfUrl(actualQuotePdfUrl);
      } catch (linkErr) {
        console.warn("Could not generate uploaded quote PDF link, using fallback:", linkErr);
      }

      await generateAgreementPDF({ 
        quote: latestQuote, 
        project, 
        agreementDate: new Date(),
        agreementClientName,
        agreementClientEmail,
        agreementClientPhone,
        agreementAddress,
        additionalTerms,
        agreementTerms,
        quotePdfUrl: actualQuotePdfUrl
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Could not create agreement PDF");
    } finally {
      setGenerating(false);
    }
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
  };

  const copyAgreementLink = async () => {
    if (!latestQuote?._id) {
      alert("No quotation found for this project.");
      return;
    }
    setCopyingAgreementLink(true);
    try {
      const actualQuotePdfUrl = await createUploadedQuotePdfLink(latestQuote);
      setQuotePdfUrl(actualQuotePdfUrl);
      const pdfBlob = await generateAgreementPDFBlob({
        quote: latestQuote,
        project,
        agreementDate: new Date(),
        agreementClientName,
        agreementClientEmail,
        agreementClientPhone,
        agreementAddress,
        additionalTerms,
        agreementTerms,
        quotePdfUrl: actualQuotePdfUrl,
      });
      const formData = new FormData();
      const clientNameForFile = String(agreementClientName || clientName(project)).replace(/[\\/:*?"<>|]/g, "-");
      formData.append("pdf", pdfBlob, `Agreement_${clientNameForFile}.pdf`);
      const res = await uploadAgreementPdfApi(latestQuote._id, formData);
      const pdfUrl = res.data?.data?.pdfUrl;
      if (!pdfUrl) throw new Error("Agreement link is not available");
      await copyTextToClipboard(pdfUrl);
      alert("Agreement link copied.");
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || e.message || "Could not copy agreement link");
    } finally {
      setCopyingAgreementLink(false);
    }
  };

  const totals = latestQuote ? getQuoteCommercialTotals(latestQuote, latestQuote.items || []) : null;

  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 16px", borderBottom: "1px solid #E5E7EB", flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1F2937" }}>Agreement</h2>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
              Create agreement from the latest quotation for this client.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={loadLatestQuote}
              disabled={loading || generating}
              style={{ padding: "8px 13px", border: "1px solid #D1D5DB", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 12, fontWeight: 700, cursor: loading || generating ? "wait" : "pointer" }}
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={createAgreement}
              disabled={!latestQuote || loading || generating}
              style={{ padding: "8px 16px", border: "none", borderRadius: 8, background: !latestQuote || loading || generating ? "#C4A8C1" : "#976793", color: "#fff", fontSize: 12, fontWeight: 700, cursor: !latestQuote || loading || generating ? "not-allowed" : "pointer" }}
            >
              {generating ? "Creating..." : "Create Agreement PDF"}
            </button>
            <button
              type="button"
              onClick={copyAgreementLink}
              disabled={!latestQuote || loading || generating || copyingAgreementLink}
              style={{ padding: "8px 16px", border: "1px solid #976793", borderRadius: 8, background: "#fff", color: "#976793", fontSize: 12, fontWeight: 700, cursor: !latestQuote || loading || generating || copyingAgreementLink ? "not-allowed" : "pointer" }}
            >
              {copyingAgreementLink ? "Copying..." : "Copy Agreement Link"}
            </button>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 12px", background: "#FAFAFA" }}>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>Client Name</div>
              <input
                type="text"
                value={agreementClientName}
                onChange={(e) => setAgreementClientName(e.target.value)}
                style={{ width: "100%", fontSize: 13, fontWeight: 600, color: "#1F2937", border: "1px solid #D1D5DB", borderRadius: 4, padding: "6px 8px", outline: "none" }}
                placeholder="Enter client name"
              />
            </div>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 12px", background: "#FAFAFA" }}>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>Client Phone</div>
              <input
                type="text"
                value={agreementClientPhone}
                onChange={(e) => setAgreementClientPhone(e.target.value)}
                style={{ width: "100%", fontSize: 13, fontWeight: 600, color: "#1F2937", border: "1px solid #D1D5DB", borderRadius: 4, padding: "6px 8px", outline: "none" }}
                placeholder="Enter client phone"
              />
            </div>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 12px", background: "#FAFAFA" }}>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>Client Email</div>
              <input
                type="email"
                value={agreementClientEmail}
                onChange={(e) => setAgreementClientEmail(e.target.value)}
                style={{ width: "100%", fontSize: 13, fontWeight: 600, color: "#1F2937", border: "1px solid #D1D5DB", borderRadius: 4, padding: "6px 8px", outline: "none" }}
                placeholder="Enter client email"
              />
            </div>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 12px", background: "#FAFAFA" }}>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>Project Address</div>
              <textarea
                value={agreementAddress}
                onChange={(e) => setAgreementAddress(e.target.value)}
                style={{ width: "100%", fontSize: 13, fontWeight: 600, color: "#1F2937", border: "1px solid #D1D5DB", borderRadius: 4, padding: "6px 8px", outline: "none", minHeight: "60px", resize: "vertical" }}
                placeholder="Enter project address"
              />
            </div>
          </div>

          {/* Agreement Terms & Conditions Section */}
          <div
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: 10,
              padding: 16,
              background: "#fff",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 14,
                paddingBottom: 10,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1F2937" }}>
                    Agreement Terms & Conditions
                  </h3>
                  {modifiedCount > 0 ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#976793",
                        background: "#F3E8F2",
                        padding: "2px 8px",
                        borderRadius: 12,
                        border: "1px solid #E8D5E6",
                      }}
                    >
                      {modifiedCount} section{modifiedCount > 1 ? "s" : ""} modified
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 11,
                        color: "#6B7280",
                        background: "#F3F4F6",
                        padding: "2px 8px",
                        borderRadius: 12,
                      }}
                    >
                      Standard defaults
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                  Edit any terms below to customize the agreement for this client. Changes update live in the PDF.
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={toggleAllCollapse}
                  style={{
                    padding: "5px 10px",
                    border: "1px solid #D1D5DB",
                    borderRadius: 6,
                    background: "#fff",
                    color: "#374151",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {AGREEMENT_SECTIONS.some((sec) => !collapsedSections[sec.key]) ? "Collapse All" : "Expand All"}
                </button>
                {modifiedCount > 0 && (
                  <button
                    type="button"
                    onClick={resetAllTerms}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #E5E7EB",
                      borderRadius: 6,
                      background: "#F9FAFB",
                      color: "#DC2626",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Reset All to Default
                  </button>
                )}
              </div>
            </div>

            {/* List of 12 Sections (2 to 13) */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {AGREEMENT_SECTIONS.map((section) => {
                const currentVal = agreementTerms[section.key] ?? DEFAULT_AGREEMENT_TERMS[section.key] ?? "";
                const isModified = currentVal !== (DEFAULT_AGREEMENT_TERMS[section.key] ?? "");
                const isCollapsed = Boolean(collapsedSections[section.key]);

                return (
                  <div
                    key={section.key}
                    style={{
                      border: isModified ? "1px solid #976793" : "1px solid #E5E7EB",
                      borderRadius: 8,
                      background: isModified ? "#FCF9FB" : "#FAFAFA",
                      overflow: "hidden",
                      transition: "border-color 0.15s ease",
                    }}
                  >
                    <div
                      onClick={() => toggleCollapse(section.key)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "9px 12px",
                        cursor: "pointer",
                        background: isModified ? "#FAF3F8" : "#F3F4F6",
                        borderBottom: isCollapsed ? "none" : "1px solid " + (isModified ? "#EADCE7" : "#E5E7EB"),
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isModified ? "#976793" : "#1F2937" }}>
                          {section.defaultNumber}. {section.title}
                        </span>
                        {isModified ? (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#976793",
                              background: "#fff",
                              border: "1px solid #C4A8C1",
                              padding: "1px 6px",
                              borderRadius: 4,
                            }}
                          >
                            Modified
                          </span>
                        ) : section.hint ? (
                          <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                            — {section.hint}
                          </span>
                        ) : null}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {isModified && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              resetSection(section.key);
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#6B7280",
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              textDecoration: "underline",
                              padding: 0,
                            }}
                            title="Revert to standard default"
                          >
                            Reset
                          </button>
                        )}
                        <span style={{ fontSize: 11, color: "#6B7280" }}>
                          {isCollapsed ? "▼ Edit" : "▲ Hide"}
                        </span>
                      </div>
                    </div>

                    {!isCollapsed && (
                      <div style={{ padding: "10px 12px" }}>
                        <textarea
                          value={currentVal}
                          onChange={(e) => handleTermChange(section.key, e.target.value)}
                          rows={section.rows || 4}
                          style={{
                            width: "100%",
                            fontSize: 12.5,
                            color: "#1F2937",
                            border: "1px solid #D1D5DB",
                            borderRadius: 6,
                            padding: "8px 10px",
                            outline: "none",
                            resize: "vertical",
                            lineHeight: 1.5,
                            fontFamily: "inherit",
                            background: "#fff",
                            boxSizing: "border-box",
                          }}
                          placeholder={`Enter custom text for ${section.title}...`}
                        />
                        <div style={{ fontSize: 10.5, color: "#9CA3AF", marginTop: 4 }}>
                          Supports bullet points (• or -), subheadings ([Heading]), and bold labels (Label: description).
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              border: additionalTerms.trim() ? "1px solid #976793" : "1px solid #E5E7EB",
              borderRadius: 8,
              padding: "10px 12px",
              background: additionalTerms.trim() ? "#FCF9FB" : "#FAFAFA",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: additionalTerms.trim() ? "#976793" : "#1F2937" }}>
                  Additional Terms and Conditions
                </span>
                {additionalTerms.trim() ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#976793",
                      background: "#fff",
                      border: "1px solid #C4A8C1",
                      padding: "1px 6px",
                      borderRadius: 4,
                    }}
                  >
                    Custom Added
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                    (Optional client-specific terms)
                  </span>
                )}
              </div>
              {additionalTerms.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    setAdditionalTerms("");
                    saveTermsToStorage(agreementTerms, "");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#6B7280",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline",
                    padding: 0,
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            <textarea
              value={additionalTerms}
              onChange={(e) => {
                setAdditionalTerms(e.target.value);
                saveTermsToStorage(agreementTerms, e.target.value);
              }}
              style={{
                width: "100%",
                fontSize: 12.5,
                color: "#1F2937",
                border: "1px solid #D1D5DB",
                borderRadius: 6,
                padding: "8px 10px",
                outline: "none",
                minHeight: "80px",
                resize: "vertical",
                lineHeight: 1.5,
                background: "#fff",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
              placeholder="Add any client-specific terms agreed on call. Put each term on a new line (e.g. • Custom term)."
            />
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "#9CA3AF" }}>Loading latest quotation...</div>
          ) : !latestQuote ? (
            <div style={{ textAlign: "center", padding: "2.5rem", color: "#9CA3AF", border: "1px dashed #D1D5DB", borderRadius: 10 }}>
              No quotation found for this project. Create a quotation first, then generate the agreement.
            </div>
          ) : (
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 14px", background: "#FAFAFA", borderBottom: "1px solid #E5E7EB", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1F2937" }}>{latestQuote.title || latestQuote.quoteCode || "Latest quotation"}</div>
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                  {latestQuote.quoteCode ? `${latestQuote.quoteCode} - ` : ""}Last updated: {fmtDate(latestQuote.updatedAt)}
                </div>
                {quotePdfUrl ? (
                  <div style={{ fontSize: 11, color: "#2563EB", marginTop: 3, wordBreak: "break-all" }}>
                    Quotation link will be included in the agreement PDF.
                  </div>
                ) : null}
              </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>Agreement amount</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#1F2937" }}>Rs. {formatInr(totals?.total || 0)}</div>
                </div>
              </div>
              <div style={{ padding: "12px 14px", fontSize: 12, color: "#6B7280", lineHeight: 1.55 }}>
                The generated agreement includes client name, project name, project address, payment terms, warranty, terms and conditions, additional terms if entered, and the core quotation scope table only. It does not include the quotation information cover page or last informational pages.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
